// GET /api/character: уровень, нагрузка на мышцы и S.P.E.C.I.A.L. (считается на сервере из данных).

import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { MuscleRank, Sex } from '@/components/muscle-map/muscles'

export type StatKey = 'S' | 'P' | 'E' | 'C' | 'I' | 'A' | 'L'

export type Stat = {
  key: StatKey
  name: string
  /** 1–10; null — нет данных, см. hint. */
  value: number | null
  month_ago: number | null
  source: string
  detail: string | null
  hint: string | null
}

export type MuscleLevel = {
  level: number
  rank: MuscleRank
  rank_label: string
  sets: number
  level_sets: number
  next_level_sets: number
  lagging: boolean
}

export type MuscleWork = { role: 'primary' | 'secondary'; sets: number }

export type Character = {
  mascot_mood?: 'idle' | 'sleepy' | 'energetic' | 'sad'
  sex: Sex
  level: number
  title: string
  xp: number
  level_xp: number
  next_level_xp: number
  workouts: number
  last_workout: {
    id: string
    title: string
    started_at: string
    xp: number
    muscles: Record<string, MuscleWork>
  } | null
  /** Подходы за 7 дней (основная 1, вспомогательная 0.5) и дни с последней нагрузки. */
  week: Record<string, { sets: number; days_since: number | null }>
  /** Восстановление, % (свежая — от 90), и через сколько часов станет свежей. */
  recovery: Record<string, { percent: number; ready_in_h: number | null }>
  /** Прокачка мышц: уровень по накопленным подходам, ранг, отстаёт ли (2+ уровня ниже медианы). */
  muscle_levels: Record<string, MuscleLevel>
  /** Почему восстановление быстрее или медленнее (Body Battery); null — обычное. */
  recovery_note: string | null
  special: Stat[]
}

export function useCharacter() {
  // Минута свежести: маскот на каждом экране не пересчитывает персонажа при каждом переходе.
  return useQuery({ queryKey: ['character'], queryFn: () => api.get<Character>('/character'), staleTime: 60_000 })
}

/** Итог тренировки для персонажа: мышцы, опыт и уровень до и после неё. */
export type SessionGain = NonNullable<Character['last_workout']> & {
  bonus_xp?: number
  xp_before: number
  xp_after: number
  level_before: number
  level: number
  title_after: string
  level_xp: number
  next_level_xp: number
}

export function useSessionGain(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: ['character', 'session', sessionId],
    queryFn: () => api.get<SessionGain>(`/character/sessions/${sessionId}`),
    enabled,
  })
}

/** Достижение: прогресс к цели; unlocked_at — когда открыто впервые (null — ещё закрыто). */
export type Achievement = {
  key: string
  icon: string
  title: string
  description: string
  target: number
  progress: number
  unit: string
  unlocked_at: string | null
}

export function useAchievements() {
  return useQuery({
    queryKey: ['character', 'achievements'],
    queryFn: () => api.get<Achievement[]>('/character/achievements'),
    staleTime: 5 * 60_000,
  })
}
